import { FC } from 'react';
import { IStepNode, IStepIntensity, IStepRecovery } from '../../../../models/programming';
import styles from './ConditioningStepsView.module.scss';

/**
 * Vista READ-ONLY di un mezzo di condizionamento step-based (EPIC 9021f6).
 * Mostra i tratti (singoli e gruppi ripetibili) con volume + intensità (% del riferimento)
 * e, se presente l'esito risolto per l'atleta, anche il valore assoluto + pace.
 */

const REF_LABEL: Record<string, string> = { vam: 'VAM', vift: 'VIFT', ftp: 'FTP' };

const volumeLabel = (n: IStepNode): string => {
  if (n.volumeValue == null) return '';
  return n.volumeMode === 'time' ? `${n.volumeValue}s` : `${n.volumeValue} m`;
};

const intensityLabel = (i?: IStepIntensity): string => {
  if (!i) return '';
  if (i.absValue != null) return `${i.absValue}${i.absUnit ? ' ' + i.absUnit : ''}`;
  if (i.ref && i.pct != null) {
    let s = `${i.pct}% ${REF_LABEL[i.ref] ?? i.ref.toUpperCase()}`;
    const r = i.resolved;
    if (r && !r.unresolved && r.absValue != null) {
      s += ` ≈ ${r.absValue} ${r.absUnit}`;
      if (r.paceFormatted) s += ` · ${r.paceFormatted}/km`;
    }
    return s;
  }
  return '';
};

const recoveryLabel = (r?: IStepRecovery): string => {
  if (!r || r.value == null) return '';
  return `rec ${r.value}${r.mode === 'distance' ? 'm' : 's'}`;
};

const SingleLine: FC<{ node: IStepNode }> = ({ node }) => {
  const parts = [
    node.label,
    volumeLabel(node),
    intensityLabel(node.intensity),
    recoveryLabel(node.recovery),
  ].filter(Boolean);
  return <div className={styles.stepLine}>{parts.join(' · ')}</div>;
};

export interface ConditioningStepsViewProps {
  steps?: IStepNode[];
}

const ConditioningStepsView: FC<ConditioningStepsViewProps> = ({ steps }) => {
  if (!steps || steps.length === 0) return null;
  return (
    <div className={styles.view}>
      {steps.map((node, idx) => {
        if (node.kind === 'group') {
          return (
            <div key={idx} className={styles.group}>
              <div className={styles.groupHeader}>{`${node.repeat ?? 1}×`}</div>
              <div className={styles.groupChildren}>
                {(node.children ?? []).map((child, cIdx) => (
                  <SingleLine key={cIdx} node={child} />
                ))}
              </div>
              {recoveryLabel(node.setRecovery) && (
                <div className={styles.setRecovery}>{recoveryLabel(node.setRecovery)} tra serie</div>
              )}
            </div>
          );
        }
        return <SingleLine key={idx} node={node} />;
      })}
    </div>
  );
};

export default ConditioningStepsView;
